import {CommitAttribution} from '@github-ui/commits/components/CommitAttribution'
import {CopySHA} from '@github-ui/commits/components/CopySHA'
import {DeferredCommitHeaderData} from '@github-ui/commits/components/DeferredCommitHeaderData'
import {useLoadSingleDeferredCommitData} from '@github-ui/commits/shared/useLoadDeferredCommitData'
import type {CommitExtended} from '@github-ui/commits/types/commit-types'
import {shortSha} from '@github-ui/commits/utils/short-sha'
import {commitPath, repositoryTreePath} from '@github-ui/paths'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {FileCodeIcon} from '@primer/octicons-react'
import {Button, Link} from '@primer/react'

import type {PullRequestAppPayload} from '../page-data/payloads/pull-request-app'
import type {FilesRoutePayload} from '../page-data/payloads/files'

type PullRequestCommitHeaderProps = {
  commit: CommitExtended
  repository: FilesRoutePayload['repository']
}

export function PullRequestCommitHeader({commit, repository}: PullRequestCommitHeaderProps) {
  const {helpUrl} = useAppPayload<PullRequestAppPayload>()
  const deferredData = useLoadSingleDeferredCommitData(
    `${commitPath({owner: repository.ownerLogin, repo: repository.name, commitish: commit.oid})}/deferred_commit_data`,
  )

  return (
    <div className="d-flex flex-column border rounded-2 overflow-hidden mb-3">
      <div className="d-flex flex-row flex-justify-between border-bottom p-3 bgColor-inset flex-items-center">
        <div className="flex-1">
          <VerifiedHTMLText html={commit.shortMessageMarkdown} className="ws-pre-wrap f5 wb-break-word" />
        </div>
        <div className="d-flex flex-items-center gap-2 ml-2">
          <Button
            as={Link}
            href={repositoryTreePath({repo: repository, action: 'tree', commitish: commit.oid})}
            leadingVisual={FileCodeIcon}
            size="small"
          >
            Browse files
          </Button>
        </div>
      </div>
      <div className="d-flex flex-row flex-justify-between p-3 f5">
        <div>
          <CommitAttribution
            commit={commit}
            repo={repository}
            settings={{
              fontColor: 'fg.default',
            }}
            textVariant="muted"
          >
            <DeferredCommitHeaderData
              deferredData={deferredData}
              oid={commit.oid}
              repo={repository}
              helpUrl={helpUrl}
            />
          </CommitAttribution>
        </div>
        <div className="d-flex flex-items-center gap-2">
          <span className="color-fg-muted">Commit</span>
          <Link
            href={commitPath({owner: repository.ownerLogin, repo: repository.name, commitish: commit.oid})}
            className="Link--primary"
          >
            <code>{shortSha(commit.oid)}</code>
          </Link>
          <CopySHA sha={commit.oid} direction="sw" />
        </div>
      </div>
    </div>
  )
}

try{ PullRequestCommitHeader.displayName ||= 'PullRequestCommitHeader' } catch {}